import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X, BookOpen, ArrowLeftRight, RotateCcw, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

const inputCls = "w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30";
const labelCls = "block text-xs uppercase tracking-widest text-forest/60 mb-1.5";
const emptyBook = { title: "", author: "", category: "General", isbn: "", total_copies: 1, description: "" };
const emptyIssue = { book_id: "", borrower_name: "", borrower_type: "student", borrower_id: "", due_date: "" };

function imageToDataUrl(file, maxW = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function LibraryManager() {
  const { settings, reload: reloadSite } = useSite();
  const libImg = (settings.images && settings.images.library) || "";
  const [imgBusy, setImgBusy] = useState(false);
  const [view, setView] = useState("books");
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);

  const [bookModal, setBookModal] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [bookForm, setBookForm] = useState(emptyBook);
  const [issueModal, setIssueModal] = useState(false);
  const [issueForm, setIssueForm] = useState(emptyIssue);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, i] = await Promise.all([api.listBooks(), api.listIssues()]);
      setBooks(b.data); setIssues(i.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNewBook = () => { setEditingBook(null); setBookForm(emptyBook); setBookModal(true); };
  const openEditBook = (b) => { setEditingBook(b.id); setBookForm({ title: b.title, author: b.author, category: b.category, isbn: b.isbn, total_copies: b.total_copies, description: b.description || "" }); setBookModal(true); };

  const saveBook = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...bookForm, total_copies: parseInt(bookForm.total_copies, 10) || 0 };
    try {
      if (editingBook) await api.updateBook(editingBook, payload);
      else await api.createBook(payload);
      toast.success(editingBook ? "Book updated" : "Book added");
      setBookModal(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const deleteBook = async (id) => {
    try { await api.deleteBook(id); toast.success("Book deleted"); await load(); }
    catch { toast.error("Failed to delete"); }
  };

  const openIssue = (bookId = "") => { setIssueForm({ ...emptyIssue, book_id: bookId }); setIssueModal(true); };

  const saveIssue = async (e) => {
    e.preventDefault();
    if (!issueForm.book_id) { toast.error("Select a book"); return; }
    setSaving(true);
    try {
      await api.createIssue(issueForm);
      toast.success("Book issued");
      setIssueModal(false); setView("issues"); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to issue"); }
    finally { setSaving(false); }
  };

  const returnBook = async (id) => {
    try { await api.returnIssue(id); toast.success("Book returned"); await load(); }
    catch { toast.error("Failed to return"); }
  };
  const deleteIssue = async (id) => {
    try { await api.deleteIssue(id); await load(); } catch { toast.error("Failed"); }
  };

  const availBooks = books.filter((b) => b.available_copies > 0);
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  const onLibImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const dataUrl = await imageToDataUrl(file);
      await api.setLibraryImage(dataUrl);
      toast.success("Library image updated");
      await reloadSite();
    } catch { toast.error("Failed to update image"); }
    finally { setImgBusy(false); e.target.value = ""; }
  };

  return (
    <div data-testid="library-manager">
      {/* Library banner image */}
      <div data-testid="library-image-editor" className="mb-8 bg-white border border-line rounded-sm p-5 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="w-40 h-24 rounded-sm overflow-hidden bg-bone border border-line shrink-0">
          {libImg ? <img src={libImg} alt="Library banner" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted"><ImageIcon size={22} /></div>}
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-xl text-forest">Library Page Image</h3>
          <p className="text-muted text-sm mt-1">This banner shows on the public Library page. You can change it anytime.</p>
        </div>
        <label className="inline-flex items-center gap-2 bg-gold text-forest px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform cursor-pointer shrink-0">
          {imgBusy ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />} Change Image
          <input data-testid="library-image-input" type="file" accept="image/*" onChange={onLibImage} className="hidden" disabled={imgBusy} />
        </label>
      </div>

      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="flex gap-3">
          <button data-testid="lib-view-books" onClick={() => setView("books")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest ${view === "books" ? "bg-forest text-bone" : "border border-line text-forest hover:bg-forest/5"}`}><BookOpen size={15} /> Books ({books.length})</button>
          <button data-testid="lib-view-issues" onClick={() => setView("issues")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest ${view === "issues" ? "bg-forest text-bone" : "border border-line text-forest hover:bg-forest/5"}`}><ArrowLeftRight size={15} /> Issued ({issues.filter((i) => i.status === "issued").length})</button>
        </div>
        <div className="flex gap-3">
          <button data-testid="issue-book-btn" onClick={() => openIssue()} className="inline-flex items-center gap-2 border border-forest text-forest px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors"><ArrowLeftRight size={15} /> Issue Book</button>
          <button data-testid="add-book-btn" onClick={openNewBook} className="inline-flex items-center gap-2 bg-gold text-forest px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform"><Plus size={15} /> Add Book</button>
        </div>
      </div>

      {view === "books" && (
        <div className="border border-line bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bone text-forest/60 uppercase text-xs tracking-widest">
              <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Author</th><th className="text-left px-4 py-3">Category</th><th className="text-center px-4 py-3">Avail/Total</th><th className="text-right px-4 py-3">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {books.map((b) => (
                <tr key={b.id} data-testid="manage-book-row">
                  <td className="px-4 py-3 font-serif text-base text-forest">{b.title}</td>
                  <td className="px-4 py-3 text-muted">{b.author}</td>
                  <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest text-gold">{b.category}</span></td>
                  <td className="px-4 py-3 text-center font-mono">{b.available_copies}/{b.total_copies}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button data-testid="issue-this-btn" disabled={b.available_copies <= 0} onClick={() => openIssue(b.id)} className="px-3 h-9 flex items-center text-xs uppercase tracking-widest border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors disabled:opacity-40">Issue</button>
                      <button data-testid="edit-book-btn" onClick={() => openEditBook(b)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors"><Pencil size={15} /></button>
                      <button data-testid="delete-book-btn" onClick={() => deleteBook(b.id)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {books.length === 0 && <p className="p-8 text-muted">No books yet.</p>}
        </div>
      )}

      {view === "issues" && (
        <div className="border border-line bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bone text-forest/60 uppercase text-xs tracking-widest">
              <tr><th className="text-left px-4 py-3">Book</th><th className="text-left px-4 py-3">Borrower</th><th className="text-left px-4 py-3">Issued</th><th className="text-left px-4 py-3">Due</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {issues.map((it) => (
                <tr key={it.id} data-testid="issue-row">
                  <td className="px-4 py-3 font-serif text-forest">{it.book_title}</td>
                  <td className="px-4 py-3">{it.borrower_name} <span className="text-muted text-xs">({it.borrower_type}{it.borrower_id ? `, ${it.borrower_id}` : ""})</span></td>
                  <td className="px-4 py-3 text-muted">{fmt(it.issue_date)}</td>
                  <td className="px-4 py-3 text-muted">{fmt(it.due_date)}</td>
                  <td className="px-4 py-3">
                    {it.status === "returned"
                      ? <span className="text-xs uppercase tracking-widest text-forest bg-forest/10 px-2 py-0.5 rounded-full">Returned {fmt(it.return_date)}</span>
                      : <span className="text-xs uppercase tracking-widest text-gold bg-gold/10 px-2 py-0.5 rounded-full">Issued</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      {it.status !== "returned" && <button data-testid="return-book-btn" onClick={() => returnBook(it.id)} className="inline-flex items-center gap-1 px-3 h-9 text-xs uppercase tracking-widest border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors"><RotateCcw size={13} /> Return</button>}
                      <button data-testid="delete-issue-btn" onClick={() => deleteIssue(it.id)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {issues.length === 0 && <p className="p-8 text-muted">No issue records yet.</p>}
        </div>
      )}

      {/* Book modal */}
      {bookModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setBookModal(false)}>
          <form data-testid="book-form" onClick={(e) => e.stopPropagation()} onSubmit={saveBook} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6"><h3 className="font-serif text-3xl text-forest">{editingBook ? "Edit Book" : "Add Book"}</h3><button type="button" onClick={() => setBookModal(false)} className="text-muted hover:text-forest"><X size={22} /></button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className={labelCls}>Title</label><input data-testid="book-title-input" required value={bookForm.title} onChange={(e) => setBookForm({ ...bookForm, title: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Author</label><input data-testid="book-author-input" value={bookForm.author} onChange={(e) => setBookForm({ ...bookForm, author: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Category</label><input data-testid="book-category-input" value={bookForm.category} onChange={(e) => setBookForm({ ...bookForm, category: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>ISBN</label><input data-testid="book-isbn-input" value={bookForm.isbn} onChange={(e) => setBookForm({ ...bookForm, isbn: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Total Copies</label><input data-testid="book-copies-input" type="number" min="0" value={bookForm.total_copies} onChange={(e) => setBookForm({ ...bookForm, total_copies: e.target.value })} className={inputCls} /></div>
              <div className="md:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="book-desc-input" rows={2} value={bookForm.description} onChange={(e) => setBookForm({ ...bookForm, description: e.target.value })} className={`${inputCls} resize-none`} /></div>
            </div>
            <button data-testid="save-book-btn" type="submit" disabled={saving} className="mt-6 w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">{saving ? "Saving…" : editingBook ? "Update Book" : "Add Book"}</button>
          </form>
        </div>
      )}

      {/* Issue modal */}
      {issueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setIssueModal(false)}>
          <form data-testid="issue-form" onClick={(e) => e.stopPropagation()} onSubmit={saveIssue} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6"><h3 className="font-serif text-3xl text-forest">Issue Book</h3><button type="button" onClick={() => setIssueModal(false)} className="text-muted hover:text-forest"><X size={22} /></button></div>
            <label className={labelCls}>Book</label>
            <select data-testid="issue-book-select" required value={issueForm.book_id} onChange={(e) => setIssueForm({ ...issueForm, book_id: e.target.value })} className={`${inputCls} mb-4`}>
              <option value="">Select a book</option>
              {availBooks.map((b) => <option key={b.id} value={b.id}>{b.title} — {b.available_copies} left</option>)}
            </select>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Borrower Name</label><input data-testid="issue-name-input" required value={issueForm.borrower_name} onChange={(e) => setIssueForm({ ...issueForm, borrower_name: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Type</label><select data-testid="issue-type-select" value={issueForm.borrower_type} onChange={(e) => setIssueForm({ ...issueForm, borrower_type: e.target.value })} className={inputCls}><option value="student">Student</option><option value="employee">Employee</option></select></div>
              <div><label className={labelCls}>ID / Card No.</label><input data-testid="issue-id-input" value={issueForm.borrower_id} onChange={(e) => setIssueForm({ ...issueForm, borrower_id: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Due Date</label><input data-testid="issue-due-input" type="date" value={issueForm.due_date} onChange={(e) => setIssueForm({ ...issueForm, due_date: e.target.value })} className={inputCls} /></div>
            </div>
            <button data-testid="save-issue-btn" type="submit" disabled={saving} className="mt-6 w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">{saving ? "Issuing…" : "Issue Book"}</button>
          </form>
        </div>
      )}
    </div>
  );
}
